import Vue from 'vue';
import VueRouter from 'vue-router';
import { useUserGetters } from '@/store';
import { isAuthenticated } from '@/services/secureStorage';
import routes from './routes';

// Legacy Auth0 actions removed – Nexus bootstrap now hydrates the store.

Vue.use(VueRouter);

export const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

const { getUser, getObjectId } = useUserGetters(['getUser', 'getObjectId']);

router.beforeEach(async (to, from, next) => {
  // Extra verbose logging during Cypress runs
  if (typeof window !== 'undefined' && (window as any).Cypress) {
    // eslint-disable-next-line no-console
    console.info('[Router] navigating from', from.fullPath, 'to', to.fullPath);
  }
  if (process.env.VUE_APP_MAINTENANCE === 'true' && to.name !== 'maintenance') {
    next({ name: 'maintenance' });
    return;
  }

  // Check if the route is a public program
  const isPublicProgram = to.name === 'publicProgram' || to.name === 'demoProgram';

  // Allow access to public programs without authentication
  if (isPublicProgram) {
    next();
    return;
  }

  // Allow direct access to the dedicated signup route when the user is not authenticated.
  // Retain legacy behavior for the legacy /login page.
  if (to.name === 'login') {
    const { isProfileComplete } = useUserGetters(['isProfileComplete']);
    // If the user is already logged in, send them to their dashboard, UNLESS
    // their profile is incomplete, in which case we allow the auth/setup
    // flow to continue.
    if ((getUser.value || isAuthenticated()) && isProfileComplete.value) {
      next({ path: '/portfolio/my-programs' });
    } else {
      // Force the legacy login experience to be shown as a modal on the landing page.
      next({
        name: 'landing',
        params: { openLoginPopup: 'true' },
        query: { ...to.query }
      });
    }
    return;
  }
  // Treat the deprecated /signup path the same as /login: surface the unified
  // auth dialog on the landing page.
  if (to.name === 'signup') {
    const { isProfileComplete } = useUserGetters(['isProfileComplete']);
    if ((getUser.value || isAuthenticated()) && isProfileComplete.value) {
      next({ path: '/portfolio/my-programs' });
    } else {
      next({
        name: 'landing',
        params: { openLoginPopup: 'true' },
        query: { ...to.query }
      });
    }
    return;
  }

  // Check if the route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

  if (requiresAuth) {
    if ((window as any).Cypress) {
      // eslint-disable-next-line no-console
      console.info('[Router] requiresAuth guard triggered – isAuthenticated=', isAuthenticated());
    }
    // If the user is *not* authenticated at all (no access token) always send
    // them to the signup flow.
    if (!isAuthenticated()) {
      // Unauthenticated users are redirected to the landing page where the
      // unified auth dialog will open automatically. We also include the
      // original destination as an encoded `redirect` query so the dialog
      // can navigate back after successful login/signup.
      next({
        name: 'landing',
        params: { openLoginPopup: 'true' },
        query: { redirect: encodeURIComponent(to.fullPath) }
      });
      return;
    }

    // If the Vuex store has not finished bootstrapping yet we allow navigation
    // to continue. Route-level guards and the consuming components will wait
    // for `getUser` to populate and handle any additional redirects.

    if (to.meta.requiresUser && getUser.value) {
      const { isProfileComplete } = useUserGetters(['isProfileComplete']);
      if (typeof window !== 'undefined' && (window as any).Cypress) {
        // eslint-disable-next-line no-console
        console.info('[Router] getter isProfileComplete.value =', isProfileComplete.value);
        // eslint-disable-next-line no-console
        console.info('[Router] raw store flag =', router.app?.$store?.state?.user?.profileComplete);
      }
      // If profile is incomplete, redirect to landing with auth popup
      if (!isProfileComplete.value) {
        next({
          name: 'landing',
          params: { openLoginPopup: 'true' },
          query: { redirect: encodeURIComponent(to.fullPath) }
        });
      } else {
        next();
      }
    } else {
      next();
    }
  } else {
    next();
  }

  // Remove existing meta tags
  document.querySelectorAll('meta[data-vue-router]').forEach(tag => tag.remove());

  // Add meta tags if they exist in the route
  if (to.meta?.metaTags) {
    to.meta.metaTags.forEach((tagDef: any) => {
      const tag = document.createElement('meta');
      Object.keys(tagDef).forEach(key => {
        tag.setAttribute(key, tagDef[key]);
      });
      tag.setAttribute('data-vue-router', '');
      document.head.appendChild(tag);
    });
  }
});

export default router;
